import {
  endOfQuarter,
  format,
  getDaysInMonth,
  getMonth,
  getQuarter,
  getYear,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  isSameMonth,
  isSameQuarter,
  isSameYear,
  startOfQuarter,
} from 'date-fns';

export function formatDateRange(
  start: Date,
  end: Date,
  today: Date = new Date(),
): string {
  // Handle invalid dates
  if (!start || !end) {
    return '';
  }

  // Handle same date case first
  if (isSameDay(start, end)) {
    const year = getYear(start);
    const isCurrentYear = isSameYear(start, today);
    return `${format(start, 'MMM d')}${isCurrentYear ? '' : `, ${year}`}`;
  }

  // Same year checks
  const sameYear = isSameYear(start, end);
  const year = getYear(start);
  const isCurrentYear = isSameYear(start, today);

  // Same quarter checks
  const sameQuarter = isSameQuarter(start, end);
  const quarter = getQuarter(start);
  const isFullQuarter =
    sameQuarter &&
    isSameDay(start, startOfQuarter(start)) &&
    isSameDay(end, endOfQuarter(end));

  // Same month checks
  const sameMonth = isSameMonth(start, end);
  const daysInMonth = getDaysInMonth(start);
  const isLeapYearFebruary =
    sameMonth &&
    getMonth(start) === 1 && // February
    daysInMonth === 29 &&
    start.getDate() === 1 &&
    end.getDate() === 29;
  const isFullMonth =
    sameMonth &&
    start.getDate() === 1 &&
    end.getDate() === daysInMonth &&
    !isLeapYearFebruary; // Exclude leap year February from full month formatting

  // Check if dates span exactly half a year
  const isHalfYear =
    sameYear &&
    ((getMonth(start) === 0 && getMonth(end) === 5 && isLastDayOfMonth(end)) ||
      (getMonth(start) === 6 &&
        getMonth(end) === 11 &&
        isLastDayOfMonth(end))) &&
    isFirstDayOfMonth(start);

  // Check if dates span exactly a year
  const isFullYear =
    sameYear &&
    start.getDate() === 1 &&
    getMonth(start) === 0 &&
    isLastDayOfMonth(end) &&
    getMonth(end) === 11;

  // Format logic for exact periods
  if (isFullMonth) {
    return isCurrentYear
      ? format(start, 'MMMM')
      : `${format(start, 'MMMM')}, ${year}`;
  }

  if (isFullQuarter) {
    return `Q${quarter}${isCurrentYear ? '' : `, ${year}`}`;
  }

  if (isHalfYear) {
    const halfYear = getMonth(start) === 0 ? 'H1' : 'H2';
    return `${year}${halfYear}`;
  }

  if (isFullYear) {
    return year.toString();
  }

  // Format logic for partial ranges
  if ((sameYear && !isCurrentYear) || isLeapYearFebruary) {
    // Different year, but range within same year, or leap year February (always show year)
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}, ${year}`;
  }

  if (sameMonth && isCurrentYear) {
    return `${format(start, 'MMM d')} - ${format(end, 'd')}`;
  }

  if (sameYear && isCurrentYear) {
    // Current year
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
  }

  // Different years
  return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`;
}
